import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { Business } from 'src/app/models/register-business';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from 'src/app/shared/error-handler/error-handler.service';
import { MiniMenuPromoModalComponent } from '../mini-menu-promo-modal/mini-menu-promo-modal.component';
import {
  MiniMenuLandingCropModalComponent,
  MiniMenuLandingCropResult
} from '../mini-menu-landing-crop-modal/mini-menu-landing-crop-modal.component';
import {
  calendarDaysSince,
  DEFAULT_MINI_MENU_SETTINGS,
  emptyPromoCrops,
  MINI_MENU_FONTS,
  MINI_MENU_PROMO_RULES,
  MiniMenuEntitlement,
  MiniMenuFoodieNewsText,
  MiniMenuFoodieNewsPresentation,
  MiniMenuMediaAsset,
  MiniMenuMenuText,
  MiniMenuPromo,
  MiniMenuPromoCrops,
  MiniMenuPromoStatus,
  MiniMenuService,
  MiniMenuSiteAdmin,
  MiniMenuSettings,
  MiniMenuTranslationBundle,
  miniMenuFontStack,
  promoStatusFromCode,
  workingDaysSince
} from 'src/app/services/mini-menu.service';
import {
  MiniMenuAppearanceModalComponent,
  MiniMenuAppearanceResult
} from '../mini-menu-appearance-modal/mini-menu-appearance-modal.component';
import { MiniMenuQrPosterModalComponent } from '../mini-menu-qr-poster-modal/mini-menu-qr-poster-modal.component';

interface KeyTitleOption {
  id: string;
  title: string;
}

interface PendingPresentationSave {
  target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl';
  file?: File;
  originalFile?: File;
  crops: MiniMenuPromoCrops;
  dimensions?: { width: number; height: number };
  operationId: string;
  expectedAsset: MiniMenuMediaAsset | null;
}

@Component({
  selector: 'app-mini-menu-settings',
  templateUrl: './mini-menu-settings.component.html',
  styleUrls: ['./mini-menu-settings.component.scss']
})
export class MiniMenuSettingsComponent implements OnChanges, OnDestroy {
  @Input() businessDetails: Business;

  @ViewChild('homepageRequestModal') homepageRequestModal!: TemplateRef<any>;
  @ViewChild('promoSubmitModal') promoSubmitModal!: TemplateRef<any>;
  @ViewChild('promoResubmitModal') promoResubmitModal!: TemplateRef<any>;
  @ViewChild('promoSuspendModal') promoSuspendModal!: TemplateRef<any>;
  @ViewChild('promoDeleteModal') promoDeleteModal!: TemplateRef<any>;
  @ViewChild('promoSuspensionExpiredModal') promoSuspensionExpiredModal!: TemplateRef<any>;

  settings: MiniMenuSettings = JSON.parse(JSON.stringify(DEFAULT_MINI_MENU_SETTINGS));
  entitlement: MiniMenuEntitlement = { available: false, plan: 'Unavailable' };
  site: MiniMenuSiteAdmin | null = null;
  isLoading = false;
  isSaving = false;
  loadFailed = false;
  translation: MiniMenuTranslationBundle = this.emptyTranslation();

  fonts: KeyTitleOption[] = MINI_MENU_FONTS;
  fontSelect: KeyTitleOption = MINI_MENU_FONTS[0];
  languages: KeyTitleOption[] = [
    { id: 'en', title: 'English' },
    { id: 'zh-CN', title: 'Chinese' }
  ];
  languageSelect: KeyTitleOption = this.languages[0];

  readonly promoRules = MINI_MENU_PROMO_RULES;
  /** The three device crops on the single landing background image. */
  landingCrops: MiniMenuPromoCrops = emptyPromoCrops();
  /** Promotion the pill menu is currently acting on, so only that row shows a busy state. */
  promoBusyId: number | null = null;
  /** Promotion awaiting a decision in one of the confirm modals. */
  promoInFlight: MiniMenuPromo | null = null;

  homepagePromo: MiniMenuPromo | null = null;
  homepageRequestNotes = '';
  homepageRequestError = '';
  private homepageModalRef?: NgbModalRef;

  private adminPreviewSubscriptions = new Subscription();
  private readonly adminPreviewUrls = new Map<string, string>();
  private siteReadSubscription?: Subscription;
  private siteReadSequence = 0;
  private loadedBusinessPartnerId: number | null = null;
  private destroyed = false;

  readonly seededDemoUrl = 'https://menu.hungery.id/staging';

  formGroup = new FormGroup({
    LocationTag: new FormControl('', Validators.maxLength(24)),
    Eyebrow: new FormControl(''),
    Subline: new FormControl('')
  });

  constructor(
    private miniMenuService: MiniMenuService,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) {}

  get publicMenuUrl(): string {
    const publicPath = this.site?.publicPath
      || (this.site?.slug ? `/${encodeURIComponent(this.site.slug)}` : '');
    if (!publicPath) {
      return '';
    }

    const publicRoot = String(environment.miniMenuPublicRoot || 'https://menu.hungery.id')
      .replace(/\/+$/, '');
    return `${publicRoot}/${publicPath.replace(/^\/+/, '')}`;
  }

  get publishedBranchTitle(): string {
    const branchId = this.site?.publishedBranchId;
    return this.site?.availableBranches?.find((branch) => branch.id === branchId)?.title || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['businessDetails']) {
      return;
    }
    const businessPartnerId = this.businessDetails?.businessPartnerId || null;
    if (businessPartnerId === this.loadedBusinessPartnerId) {
      return;
    }
    this.cancelSiteRead();
    this.loadedBusinessPartnerId = businessPartnerId;
    this.clearAdminMediaPreviews();
    this.clearPendingPresentationSave();
    this.site = null;
    this.entitlement = { available: false, plan: 'Unavailable' };
    this.loadFailed = false;
    this.settings = JSON.parse(JSON.stringify(DEFAULT_MINI_MENU_SETTINGS));
    this.translation = this.emptyTranslation();
    this.landingCrops = emptyPromoCrops();
    this.patchFromSettings();
    if (businessPartnerId) {
      this.loadSite(businessPartnerId);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.homepageModalRef?.dismiss();
    this.cancelSiteRead();
    this.clearAdminMediaPreviews();
    this.clearPendingPresentationSave();
  }

  /** Check subscription changes when the vendor returns from Marketplace, preserving drafts. */
  refreshAvailability(): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId || this.destroyed || this.isLoading || this.isSaving || this.promoBusyId !== null
      || this.modalService.hasOpenModals()) {
      return;
    }
    this.loadSite(businessPartnerId, true);
  }

  save(): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId || !this.site?.isAvailable || this.isSaving || this.isLoading || this.loadFailed) {
      return;
    }

    this.collectIntoSettings();
    this.isSaving = true;
    const enabledLanguages = [
      { code: 'en', title: 'English', shortTitle: 'EN', sortOrder: 0 },
      ...(this.settings.chineseEnabled
        ? [{ code: 'zh-CN', title: 'Chinese', shortTitle: 'ZH.CN', sortOrder: 10 }]
        : [])
    ];
    const defaultLanguage = enabledLanguages.some((language) => language.code === this.settings.defaultLanguage)
      ? this.settings.defaultLanguage
      : 'en';
    this.miniMenuService.updatePresentation(businessPartnerId, {
      locationTag: this.settings.locationTag,
      eyebrow: this.settings.eyebrow,
      subline: this.settings.subline,
      font: this.settings.theme.font,
      backgroundColor: this.settings.theme.backgroundColor,
      uiColor: this.settings.theme.uiColor,
      contentColor: this.settings.theme.contentColor,
      accentColor: this.settings.theme.accentColor,
      rowVersion: this.site.presentation?.rowVersion || null
    }).pipe(
      switchMap(() => this.miniMenuService.updateLanguages(
        businessPartnerId,
        defaultLanguage,
        enabledLanguages
      )),
      switchMap(() => this.miniMenuService.updateFoodieNewsOrder(businessPartnerId, this.settings.promos)),
      switchMap((site) => this.settings.chineseEnabled
        ? this.miniMenuService.updateTranslations(businessPartnerId, this.translation)
        : of(site))
    ).subscribe({
      next: (site) => {
        this.applySite(site);
        this.isSaving = false;
        this.toastr.success('MiniMenu details saved.');
        // Saving assigns ids to brand-new promotions, so this is the first moment they can
        // be submitted. Same shape as Save Details then "Publish business?" on the form.
        this.promptSubmitDrafts();
      },
      error: (error) => {
        this.isSaving = false;
        this.toastr.error(ErrorHandlerService.bodyMessage(error) || 'MiniMenu details could not be saved.');
      }
    });
  }

  togglePublished(event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextPublished = input.checked;
    const businessPartnerId = this.businessDetails?.businessPartnerId;

    if (!this.site?.isAvailable || !businessPartnerId || !this.site.publishedBranchId || !this.site.slug
      || this.isSaving || this.isLoading || this.loadFailed) {
      input.checked = this.site?.isPublished === true;
      return;
    }

    this.isSaving = true;
    this.miniMenuService.updateSite(businessPartnerId, {
      publishedBranchId: this.site.publishedBranchId,
      slug: this.site.slug,
      isPublished: nextPublished,
      defaultLanguage: this.site.defaultLanguage || 'en'
    }).subscribe({
      next: (site) => {
        this.applySite(site);
        this.isSaving = false;
        this.toastr.success(site.isPublished ? 'MiniMenu is published.' : 'MiniMenu is hidden.');
      },
      error: (error) => {
        input.checked = this.site?.isPublished === true;
        this.isSaving = false;
        this.toastr.error(error?.error?.message || 'MiniMenu publishing could not be updated.');
      }
    });
  }

  private cancelSiteRead(): void {
    this.siteReadSequence++;
    this.siteReadSubscription?.unsubscribe();
    this.siteReadSubscription = undefined;
    this.isLoading = false;
  }

  private loadSite(businessPartnerId: number, preserveSettings = false): void {
    this.cancelSiteRead();
    const sequence = this.siteReadSequence;
    const isCurrent = () => !this.destroyed
      && sequence === this.siteReadSequence
      && businessPartnerId === this.businessDetails?.businessPartnerId;
    const fail = () => {
      if (!isCurrent()) {
        return;
      }
      // Retain local drafts for a retry. The template shows an error instead of claiming
      // the service is available or asking the vendor to subscribe after a failed read.
      this.loadFailed = true;
      this.isLoading = false;
    };
    this.isLoading = true;
    this.loadFailed = false;
    this.siteReadSubscription = this.miniMenuService.getSite(businessPartnerId).pipe(take(1)).subscribe({
      next: (site) => {
        if (!isCurrent()) {
          return;
        }
        if (!site || site.businessPartnerId !== businessPartnerId
          || typeof site.isAvailable !== 'boolean' || typeof site.isPublished !== 'boolean') {
          fail();
          return;
        }
        if (this.isSaving || this.promoBusyId !== null) {
          this.isLoading = false;
          return;
        }
        if (!preserveSettings || !this.site || this.site.miniMenuSiteId !== site.miniMenuSiteId
          || (!this.site.isConfigured && site.isConfigured)) {
          this.applySite(site);
        } else {
          // Subscription state may change elsewhere, but presentation, translations and
          // promotions on this page can contain unsaved work and keep their original versions.
          this.site = {
            ...this.site,
            isConfigured: site.isConfigured,
            isAvailable: site.isAvailable,
            isPublished: site.isPublished,
            businessName: site.businessName,
            availableBranches: site.availableBranches,
            publishedBranchId: site.publishedBranchId,
            slug: site.slug,
            publicPath: site.publicPath,
            defaultLanguage: site.defaultLanguage,
            orderingAvailable: site.orderingAvailable,
            orderingMessage: site.orderingMessage
          };
          this.entitlement = {
            available: site.isAvailable,
            plan: site.isAvailable ? 'Available' : 'Unavailable'
          };
          this.settings.enabled = site.isPublished;
        }
        this.isLoading = false;
      },
      error: fail,
      complete: () => { if (isCurrent() && this.isLoading) { fail(); } }
    });
  }

  private applySite(site: MiniMenuSiteAdmin): void {
    this.clearAdminMediaPreviews();
    this.site = site;
    this.entitlement = {
      available: site.isAvailable,
      plan: site.isAvailable ? 'Available' : 'Unavailable'
    };
    this.settings = this.miniMenuService.settingsFromSite(site);
    const landing = site.presentation?.landingBackground;
    this.landingCrops = {
      mobile: landing?.mobileCrop || null,
      tablet: landing?.tabletCrop || null,
      tabletPortrait: landing?.tabletPortraitCrop || null
    };
    this.translation = site.translations?.find((item) => item.languageCode === 'zh-CN')
      || this.emptyTranslation();
    this.patchFromSettings();
    this.loadAdminMediaPreviews(site);
    this.promptExpiredSuspensions();
  }

  /**
   * Draft media is private, so its public URL correctly returns 404. Fetch every settings
   * preview through HttpClient instead: the auth interceptor supplies the bearer token even
   * when staging and its API are on different domains.
   */
  private loadAdminMediaPreviews(site: MiniMenuSiteAdmin): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId) {
      return;
    }

    this.loadAdminMediaPreview(
      'presentation-background',
      businessPartnerId,
      site.presentation?.landingBackground,
      (url) => { this.settings.teaserBackgroundUrl = url; }
    );
    this.loadAdminMediaPreview(
      'presentation-landing-logo',
      businessPartnerId,
      site.presentation?.landingLogo,
      (url) => { this.settings.theme.landingLogoUrl = url; }
    );
    this.loadAdminMediaPreview(
      'presentation-banner-logo',
      businessPartnerId,
      site.presentation?.bannerLogo,
      (url) => { this.settings.theme.bannerLogoUrl = url; }
    );
    this.settings.promos.forEach((promo) => this.loadAdminMediaPreview(
      `promotion-${promo.promotionId}`,
      businessPartnerId,
      promo.mediaAsset,
      (url) => { promo.image = url; }
    ));
  }

  private loadAdminMediaPreview(
    key: string,
    businessPartnerId: number,
    asset: MiniMenuMediaAsset | null | undefined,
    applyUrl: (url: string) => void
  ): void {
    if (!asset) {
      return;
    }
    this.revokeAdminMediaPreview(key);
    const preview = environment.miniMenuImagePipelineEnabled
      ? this.miniMenuService.getAdminMediaPreview(businessPartnerId, asset, asset.role === 2 || asset.role === 3 ? 'logo' : 'thumbnail')
      : this.miniMenuService.getAdminMediaPreview(businessPartnerId, asset);
    const subscription = preview.subscribe({
      next: (blob) => {
        if (!blob?.size) {
          return;
        }
        const url = URL.createObjectURL(blob);
        this.adminPreviewUrls.set(key, url);
        applyUrl(url);
      },
      // A missing private preview should leave the placeholder visible without turning a
      // successful settings load into an error for the entire form.
      error: () => { /* no preview available */ }
    });
    this.adminPreviewSubscriptions.add(subscription);
  }

  private revokeAdminMediaPreview(key: string): void {
    const url = this.adminPreviewUrls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      this.adminPreviewUrls.delete(key);
    }
  }

  private clearAdminMediaPreviews(): void {
    this.adminPreviewSubscriptions.unsubscribe();
    this.adminPreviewSubscriptions = new Subscription();
    this.adminPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.adminPreviewUrls.clear();
  }

  addPromotion(): void {
    this.openPromoModal(this.miniMenuService.createPromo());
  }

  openPromoModal(existing?: MiniMenuPromo, index?: number): void {
    const promo = existing || this.miniMenuService.createPromo();
    const targetIndex = typeof index === 'number'
      ? index
      : this.settings.promos.findIndex((item) => item.id === promo.id);

    const modalRef = this.modalService.open(MiniMenuPromoModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-promo-modal',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
    modalRef.componentInstance.businessPartnerId = this.businessDetails?.businessPartnerId;
    modalRef.componentInstance.isNew = targetIndex < 0;
    modalRef.componentInstance.promo = { ...promo, crops: { ...promo.crops } };
    // List previews are bounded. Fetch the coordinate master only on edit.
    if (environment.miniMenuImagePipelineEnabled && promo.mediaAsset && !promo.pendingImageFile) {
      modalRef.componentInstance.promo.image = null;
      modalRef.componentInstance.isCompressing = true;
      this.miniMenuService.getAdminMediaPreview(this.businessDetails!.businessPartnerId, promo.mediaAsset).subscribe({
        next: (blob) => { const url = URL.createObjectURL(blob); modalRef.componentInstance.imageSource = url;
          modalRef.componentInstance.promo.image = url; modalRef.componentInstance.isImageReady = true; modalRef.componentInstance.isCompressing = false;
          modalRef.result.finally(() => URL.revokeObjectURL(url)).catch(() => {}); },
        error: () => { modalRef.componentInstance.isCompressing = false; modalRef.componentInstance.imageLoadFailed = true;
          this.toastr.error('The photo could not be loaded.'); }
      });
    }

    modalRef.result.then(
      (saved: MiniMenuPromo) => {
        const businessPartnerId = this.businessDetails?.businessPartnerId;
        if (!saved || !businessPartnerId) {
          return;
        }
        this.isSaving = true;
        this.miniMenuService.saveFoodieNews(businessPartnerId, saved, Math.max(0, targetIndex)).pipe(
          switchMap((record) => {
            saved.promotionId = record.foodieNewsId;
            saved.rowVersion = record.rowVersion;
            if (saved.pendingImageFile && environment.miniMenuImagePipelineEnabled) {
              saved.imageOperationId ||= crypto.randomUUID();
              if (!saved.imageDimensions) return throwError(() => new Error('Prepare the photo again before saving.'));
              return this.miniMenuService.uploadPreparedMedia(businessPartnerId, 4, saved.pendingImageFile,
                saved.crops, saved.title, saved.mediaId || 0, saved.imageOperationId, saved.imageDimensions, record.foodieNewsId);
            }
            if (saved.pendingImageFile) {
              return this.miniMenuService.uploadMedia(
                businessPartnerId,
                4,
                saved.pendingImageFile,
                record.foodieNewsId
              ).pipe(
                tap((asset) => this.acceptPromotionMedia(saved, asset)),
                switchMap((asset) => this.miniMenuService.updateMediaCrop(
                  businessPartnerId,
                  asset.id,
                  saved.crops,
                  saved.title
                ))
              );
            }
            if (!saved.image && saved.mediaId) {
              return this.miniMenuService.deleteMedia(businessPartnerId, saved.mediaId);
            }
            if (saved.image && saved.mediaId) {
              return environment.miniMenuImagePipelineEnabled
                ? this.miniMenuService.updateMediaCrop(businessPartnerId, saved.mediaId, saved.crops, saved.title, saved.mediaAsset?.mediaVersion)
                : this.miniMenuService.updateMediaCrop(businessPartnerId, saved.mediaId, saved.crops, saved.title);
            }
            return of(null);
          }),
          tap((asset) => {
            if (asset) this.acceptPromotionMedia(saved, asset);
            else if (!saved.image) { saved.mediaId = undefined; saved.mediaAsset = null; }
          }),
          switchMap(() => this.miniMenuService.getSite(businessPartnerId))
        ).subscribe({
          next: (site) => {
            this.applySite(site);
            this.isSaving = false;
            this.toastr.success('What’s On draft saved.');
          },
          error: (error) => this.handlePromotionSaveError(businessPartnerId, saved, error)
        });
      },
      () => { /* dismissed — no change */ }
    );
  }

  private acceptPromotionMedia(promo: MiniMenuPromo, asset: MiniMenuMediaAsset): void {
    promo.mediaId = asset.id;
    promo.mediaAsset = asset;
    promo.pendingImageFile = null;
  }

  /** Keep the local edit before any recovery request, including when the browser is offline. */
  private handlePromotionSaveError(
    businessPartnerId: number,
    saved: MiniMenuPromo,
    error: any
  ): void {
    const message = ErrorHandlerService.bodyMessage(error) || 'The promotion could not be saved. Please try again.';
    const pending = this.settings.promos.find(p => p.id === saved.id
      || (!!saved.promotionId && p.promotionId === saved.promotionId));
    // An editing master blob is revoked when its modal closes. Keep the list's
    // own preview for coordinate edits; replacement files have a data-URL preview.
    if (pending) {
      const image = saved.image?.startsWith('blob:') ? pending.image : saved.image;
      Object.assign(pending, saved, { image });
    } else {
      this.settings.promos.push(saved);
    }
    const retained = pending || saved;
    if (!saved.promotionId) {
      this.isSaving = false;
      this.toastr.error(message);
      return;
    }

    this.miniMenuService.getSite(businessPartnerId).subscribe({
      next: (site) => {
        const fresh = site.foodieNews?.find(p => p.foodieNewsId === saved.promotionId);
        if (fresh) retained.rowVersion = fresh.rowVersion;
        this.isSaving = false;
        this.toastr.error(message);
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error(message);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // What's On status pill
  // ---------------------------------------------------------------------------

  promoStatusLabel(promo: MiniMenuPromo): string {
    const labels: Record<MiniMenuPromoStatus, string> = {
      draft: 'Draft',
      review: 'Pending review',
      live: 'Published',
      promoted: 'Promoted',
      suspended: 'Suspended',
      deleted: 'Deleted'
    };
    return labels[promo.status] || 'Draft';
  }

  /** A promotion Evolution has not numbered yet cannot be submitted or suspended. */
  isPromoSaved(promo: MiniMenuPromo): boolean {
    return (promo.promotionId || 0) > 0 && !promo.isLegacy;
  }

  /**
   * The one line of context shown beside the pill. Every row uses this same shape, and the
   * pill popup stays a plain list of actions rather than carrying explanatory text.
   */
  promoNote(promo: MiniMenuPromo): string {
    if (promo.isLegacy) {
      return 'Legacy Foodie News; its existing schedule remains unchanged.';
    }
    if (!this.isPromoSaved(promo)) {
      return 'Not saved yet';
    }
    switch (promo.status) {
      case 'review': {
        const waited = this.reviewWaitingDays(promo);
        const target = this.promoRules.resubmitAfterWorkingDays;
        return waited >= target
          ? 'You can re-submit this review'
          : `Waiting ${waited} of ${target} working days`;
      }
      case 'suspended':
        return this.suspensionDaysLeft(promo)
          ? `Hidden for ${this.suspensionDaysLeft(promo)} more day(s)`
          : 'Needs a decision';
      case 'deleted':
        return this.deleteRestoreDaysLeft(promo)
          ? `Restorable for ${this.deleteRestoreDaysLeft(promo)} more day(s)`
          : 'Awaiting permanent removal';
      default:
        return '';
    }
  }

  homepageStatusLabel(promo: MiniMenuPromo): string {
    switch (promo.homepageStatus) {
      case 1: return 'Homepage request pending review';
      case 2:
        if (promo.homepageExpiresOn && Date.parse(promo.homepageExpiresOn) <= Date.now()) {
          return 'Homepage placement expired';
        }
        return promo.isHomepageVisible ? 'Visible in HUNGERY Foodie News' : 'Homepage placement approved';
      case 3: return 'Homepage request declined';
      case 4: return 'Homepage placement removed';
      default: return '';
    }
  }

  canRequestHomepage(promo: MiniMenuPromo): boolean {
    if (environment.homepageRequestsEnabled !== true || !this.isPromoSaved(promo) || !promo.rowVersion || promo.isHomepageSourceEligible !== true
      || (promo.status !== 'live' && promo.status !== 'promoted')
      || (promo.publishTo && Date.parse(promo.publishTo) <= Date.now())
      || (promo.publishFrom && Date.parse(promo.publishFrom) > Date.now())
      || promo.homepageStatus === 1) {
      return false;
    }
    return promo.homepageStatus !== 2
      || (!!promo.homepageExpiresOn && Date.parse(promo.homepageExpiresOn) <= Date.now());
  }

  openHomepageRequest(promo: MiniMenuPromo): void {
    if (!this.canRequestHomepage(promo) || this.isSaving || this.promoBusyId !== null || this.homepageModalRef) {
      return;
    }
    this.homepagePromo = promo;
    this.homepageRequestNotes = '';
    this.homepageRequestError = '';
    this.homepageModalRef = this.modalService.open(this.homepageRequestModal, {
      windowClass: 'project-modal',
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      ariaLabelledBy: 'homepage-request-title'
    });
    const clear = () => { this.homepageModalRef = undefined; this.homepagePromo = null; };
    this.homepageModalRef.result.then(clear, clear);
  }

  confirmHomepageRequest(): void {
    const promo = this.homepagePromo;
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!promo || !businessPartnerId || !this.canRequestHomepage(promo)
      || this.isSaving || this.promoBusyId !== null || this.homepageRequestNotes.length > 1000) {
      return;
    }
    this.homepageRequestError = '';
    this.promoBusyId = promo.promotionId;
    this.miniMenuService.requestFoodieNewsHomepage(businessPartnerId, promo, this.homepageRequestNotes).subscribe({
      next: (record) => {
        this.promoBusyId = null;
        this.applyHomepageRecord(promo, record);
        this.homepageModalRef?.close();
        this.toastr.success('Homepage request sent to HUNGERY for review.');
      },
      error: (error) => {
        this.promoBusyId = null;
        this.homepageRequestError = ErrorHandlerService.bodyMessage(error)
          || 'The homepage request could not be submitted. Please try again.';
        if (error?.status === 409) {
          this.miniMenuService.getSite(businessPartnerId).subscribe({
            next: (site) => {
              const fresh = site.foodieNews?.find((item) => item.foodieNewsId === promo.promotionId);
              if (fresh) {
                this.applyHomepageRecord(promo, fresh, true);
              } else {
                promo.isHomepageSourceEligible = false;
              }
            },
            error: () => { this.homepageRequestError += ' Close this window and refresh before trying again.'; }
          });
        }
      }
    });
  }

  // A homepage request only updates this saved post. Keep other form edits and translations.
  private applyHomepageRecord(promo: MiniMenuPromo, record: MiniMenuFoodieNewsPresentation, refreshMedia = false): void {
    Object.assign(promo, {
      title: record.title,
      badge: record.badge,
      when: record.availabilityText,
      lead: record.lead,
      body: record.body,
      status: promoStatusFromCode(record.status),
      publishFrom: record.publishFrom,
      publishTo: record.publishTo,
      rowVersion: record.rowVersion,
      homepageStatus: record.homepageStatus,
      homepageRequestedOn: record.homepageRequestedOn,
      homepageExpiresOn: record.homepageExpiresOn,
      homepageReviewNotes: record.homepageReviewNotes,
      isHomepageVisible: record.isHomepageVisible,
      isHomepageSourceEligible: record.isHomepageSourceEligible
    });
    if (refreshMedia || promo.mediaId !== record.media?.id) {
      promo.mediaId = record.media?.id;
      promo.mediaAsset = record.media;
      promo.crops = {
        mobile: record.media?.mobileCrop || null,
        tablet: record.media?.tabletCrop || null,
        tabletPortrait: record.media?.tabletPortraitCrop || null
      };
      promo.image = record.media ? null : record.baseImage;
      this.loadAdminMediaPreview(`promotion-${promo.promotionId}`, this.businessDetails.businessPartnerId,
        record.media, (url) => { promo.image = url; });
    }
  }

  /** Working days a pending promotion has been waiting on an approver. */
  reviewWaitingDays(promo: MiniMenuPromo | null): number {
    return workingDaysSince(promo?.submittedOn);
  }

  /** The re-submit prompt only unlocks once the approval SLA has actually lapsed. */
  canResubmit(promo: MiniMenuPromo): boolean {
    return this.isPromoSaved(promo)
      && this.reviewWaitingDays(promo) >= this.promoRules.resubmitAfterWorkingDays;
  }

  /** Days left before a suspended promotion forces a publish-or-delete decision. */
  suspensionDaysLeft(promo: MiniMenuPromo): number {
    return Math.max(0, this.promoRules.suspendDays - calendarDaysSince(promo.suspendedOn));
  }

  isSuspensionExpired(promo: MiniMenuPromo): boolean {
    return promo.status === 'suspended'
      && !!promo.suspendedOn
      && calendarDaysSince(promo.suspendedOn) >= this.promoRules.suspendDays;
  }

  /** Days left to restore a deleted promotion before Evolution purges it. */
  deleteRestoreDaysLeft(promo: MiniMenuPromo): number {
    return Math.max(0, this.promoRules.deleteRestoreDays - calendarDaysSince(promo.deletedOn));
  }

  canRestoreDeleted(promo: MiniMenuPromo): boolean {
    return promo.status === 'deleted' && this.deleteRestoreDaysLeft(promo) > 0;
  }

  // ---------------------------------------------------------------------------
  // Pill actions. Publishing always goes through the ERP approval queue.
  // ---------------------------------------------------------------------------

  /** Submits one promotion for approval — the promo equivalent of business/publish. */
  submitForReview(promo: MiniMenuPromo, onDone?: () => void): void {
    if (!this.isPromoSaved(promo)) {
      this.toastr.info('Save MiniMenu details first, then submit this promotion for review.');
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.submitFoodieNews(businessPartnerId, promo),
      'Promotion submitted for review. It goes live once HUNGERY approves it.',
      'The promotion could not be submitted for review.',
      onDone
    );
  }

  openResubmitPrompt(promo: MiniMenuPromo): void {
    this.promoInFlight = promo;
    this.openConfirm(this.promoResubmitModal);
  }

  confirmResubmit(): void {
    const promo = this.promoInFlight;
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.resubmitFoodieNews(businessPartnerId, promo),
      'Review request re-sent.',
      'The review request could not be re-sent.'
    );
  }

  openSuspendPrompt(promo: MiniMenuPromo): void {
    this.promoInFlight = promo;
    this.openConfirm(this.promoSuspendModal);
  }

  confirmSuspend(): void {
    const promo = this.promoInFlight;
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.suspendFoodieNews(businessPartnerId, promo),
      `Promotion suspended. It stays hidden for ${this.promoRules.suspendDays} days.`,
      'The promotion could not be suspended.'
    );
  }

  openDeletePrompt(promo: MiniMenuPromo, index: number): void {
    // An unsaved draft has nothing on the server to soft-delete; drop it locally.
    if (!this.isPromoSaved(promo)) {
      this.settings.promos.splice(index, 1);
      return;
    }
    this.promoInFlight = promo;
    this.openConfirm(this.promoDeleteModal);
  }

  confirmDelete(): void {
    const promo = this.promoInFlight;
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.deleteFoodieNews(businessPartnerId, promo),
      `Promotion deleted. You can restore it for ${this.promoRules.deleteRestoreDays} days.`,
      'The promotion could not be deleted.'
    );
  }

  /** Brings a suspended or recently deleted promotion back through approval. */
  restorePromo(promo: MiniMenuPromo | null): void {
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.restoreFoodieNews(businessPartnerId, promo),
      'Promotion sent back for review.',
      'The promotion could not be restored.'
    );
  }

  promoThumb(promo: MiniMenuPromo): string | null {
    return promo.image || null;
  }

  /**
   * Every pill action hits Evolution and answers with the refreshed site, so one runner
   * covers submit / re-submit / suspend / restore / delete.
   */
  private runPromoAction(
    promo: MiniMenuPromo,
    call: (businessPartnerId: number) => Observable<MiniMenuSiteAdmin>,
    successMessage: string,
    failureMessage: string,
    onDone?: () => void
  ): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId || this.promoBusyId !== null) {
      return;
    }
    this.promoBusyId = promo.promotionId;
    call(businessPartnerId).subscribe({
      next: (site) => {
        this.promoBusyId = null;
        this.promoInFlight = null;
        this.applySite(site);
        this.toastr.success(successMessage);
        onDone?.();
      },
      error: (error) => {
        this.promoBusyId = null;
        this.promoInFlight = null;
        this.toastr.error(ErrorHandlerService.bodyMessage(error) || failureMessage);
      }
    });
  }

  private openConfirm(template: TemplateRef<any>, dismissible = true): void {
    if (!template) {
      return;
    }
    this.modalService.open(template, {
      windowClass: 'project-modal warning-section-modal',
      centered: true,
      backdropClass: 'primary-backdrop',
      backdrop: dismissible ? true : 'static',
      keyboard: dismissible
    });
  }

  /** After a save, offer to submit the drafts that have never been through approval. */
  private promptSubmitDrafts(): void {
    // An unresolved suspension takes priority — never stack two prompts.
    if (this.promoInFlight || !this.submittableDrafts().length) {
      return;
    }
    this.openConfirm(this.promoSubmitModal);
  }

  submittableDrafts(): MiniMenuPromo[] {
    return this.settings.promos.filter((promo) => promo.status === 'draft' && this.isPromoSaved(promo));
  }

  /**
   * Submits every saved draft, one request at a time. Each response replaces the promo list,
   * so the next draft is picked from the refreshed list rather than a stale snapshot.
   */
  confirmSubmitDrafts(): void {
    const next = this.submittableDrafts()[0];
    if (!next) {
      return;
    }
    this.submitForReview(next, () => this.confirmSubmitDrafts());
  }

  /**
   * A suspension that has run its course must be resolved. This fires when the MiniMenu
   * form loads — the surface the vendor lands on after signing in — and the modal cannot
   * be dismissed without choosing publish or delete.
   */
  private promptExpiredSuspensions(): void {
    const expired = this.settings.promos.find((promo) => this.isSuspensionExpired(promo));
    if (!expired || this.promoInFlight) {
      return;
    }
    this.promoInFlight = expired;
    this.openConfirm(this.promoSuspensionExpiredModal, false);
  }

  onFontChange(option: KeyTitleOption): void {
    this.fontSelect = option || this.fonts[0];
    this.settings.theme.font = this.fontSelect.id;
  }

  /** CSS stack for the chosen theme font, so the section preview renders in it. */
  get themeFontStack(): string {
    return miniMenuFontStack(this.settings.theme.font);
  }

  /** Opens the appearance editor: controls beside a live miniature of the landing screen. */
  openAppearanceModal(): void {
    const modalRef = this.modalService.open(MiniMenuAppearanceModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-appearance-modal',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
    modalRef.componentInstance.theme = JSON.parse(JSON.stringify(this.settings.theme));
    modalRef.componentInstance.businessName = this.businessDetails?.name || 'Your Business';
    modalRef.componentInstance.locationTag = this.settings.locationTag;
    modalRef.componentInstance.eyebrow = this.settings.eyebrow;
    modalRef.componentInstance.subline = this.settings.subline;

    modalRef.result.then(
      (result: MiniMenuAppearanceResult) => {
        if (!result) {
          return;
        }
        this.settings.theme.font = result.font;
        this.settings.theme.backgroundColor = result.backgroundColor;
        this.settings.theme.uiColor = result.uiColor;
        this.settings.theme.contentColor = result.contentColor;
        this.settings.theme.accentColor = result.accentColor;
        this.fontSelect = this.fonts.find((font) => font.id === result.font) || this.fonts[0];
      },
      () => { /* dismissed — theme unchanged */ }
    );
  }

  /**
   * Opens the QR poster designer: a live poster composite beside its controls. Nothing is
   * persisted — the modal's product is a downloaded SVG, so there is no result to handle.
   */
  openQrPosterModal(): void {
    const modalRef = this.modalService.open(MiniMenuQrPosterModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-qr-poster-modal',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
    modalRef.componentInstance.businessName = this.businessDetails?.name || 'Your Business';
    modalRef.componentInstance.businessDetail = [this.businessDetails?.cityName, this.businessDetails?.cuisineName]
      .filter(Boolean)
      .join(' \u00b7 ');
    modalRef.componentInstance.menuUrl = this.publicMenuUrl;
    modalRef.result.then(() => { /* closed */ }, () => { /* dismissed */ });
  }

  onLanguageChange(option: KeyTitleOption): void {
    this.languageSelect = option || this.languages[0];
    this.settings.defaultLanguage = this.languageSelect.id as MiniMenuSettings['defaultLanguage'];
  }

  /**
   * Landing background: ONE image carrying three device crops, per MENU_IMAGE_FRAME_RATIOS.landing.
   * The picked file goes through the crop step first, then uploads and has its crops stored — the
   * same two-call shape the promo image uses (upload, then crop).
   */
  async onImageSelected(event: Event, target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl'): Promise<void> {
    if (this.isSaving) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId) return;
    const role = target === 'landingLogoUrl' ? 2 : target === 'bannerLogoUrl' ? 3 : 1;
    const pending = this.pendingPresentationSave;
    const original = pending?.originalFile;
    if (pending?.target === target && original && file.name === original.name
      && file.size === original.size && file.lastModified === original.lastModified) {
      if (role === 1) this.editLandingCrops();
      else this.persistPresentationSave(businessPartnerId, pending);
      input.value = '';
      return;
    }
    this.isSaving = true;
    let prepared;
    try { prepared = await this.miniMenuService.prepareMediaFile(file, businessPartnerId, role); }
    catch (error: any) { this.isSaving = false; input.value = '';
      this.toastr.error(error?.error?.message || error?.message || 'The photo could not be prepared.'); return; }
    const source = prepared.file;
    const dimensions = { width: prepared.width, height: prepared.height };
    if (role !== 1) {
      const asset = this.presentationMedia(target);
      if (environment.miniMenuImagePipelineEnabled) {
        this.persistPresentationSave(businessPartnerId, { target, file: source, crops: emptyPromoCrops(),
          originalFile: file, dimensions, operationId: crypto.randomUUID(), expectedAsset: asset });
        return;
      }
      const upload = environment.miniMenuImagePipelineEnabled
        ? this.miniMenuService.uploadPreparedMedia(businessPartnerId, role, source, emptyPromoCrops(), 'Logo',
          asset?.id || 0, crypto.randomUUID(), dimensions)
        : this.miniMenuService.uploadMedia(businessPartnerId, role, source);
      upload.subscribe({ next: updated => { this.setPresentationMedia(target, updated); this.isSaving = false; },
        error: () => { this.isSaving = false; this.toastr.error('The logo could not be saved. Please retry.'); } });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.isSaving = false;
      const modalRef = this.openLandingCropModal(String(reader.result || ''), emptyPromoCrops());
      modalRef.result.then(
        (result: MiniMenuLandingCropResult) => {
          if (result.removed) {
            return;
          }
          this.uploadLandingBackground(businessPartnerId, result.replacementFile || source, result.crops,
            result.imageDimensions || dimensions, result.imageOperationId, result.replacementFile ? undefined : file);
        },
        () => { /* dismissed — nothing uploaded */ }
      );
    };
    reader.onerror = () => {
      this.isSaving = false;
      this.toastr.error('The selected image could not be prepared.');
    };
    reader.readAsDataURL(source);
  }

  private openLandingCropModal(imageSource: string, crops: MiniMenuPromoCrops): NgbModalRef {
    const modalRef = this.modalService.open(MiniMenuLandingCropModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-landing-crop-modal',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.businessPartnerId = this.businessDetails?.businessPartnerId;
    modalRef.componentInstance.imageSource = imageSource;
    modalRef.componentInstance.crops = crops;
    return modalRef;
  }

  pendingPresentationSave: PendingPresentationSave | null = null;

  private clearPendingPresentationSave(): void {
    this.pendingPresentationSave = null;
  }

  private persistPresentationSave(owner: number, pending: NonNullable<MiniMenuSettingsComponent['pendingPresentationSave']>): void {
    if (pending !== this.pendingPresentationSave) this.clearPendingPresentationSave();
    this.pendingPresentationSave = pending; this.isSaving = true;
    const role = pending.target === 'landingLogoUrl' ? 2 : pending.target === 'bannerLogoUrl' ? 3 : 1;
    const alt = role === 1 ? 'Landing background' : 'Logo';
    const upload = !pending.file && pending.expectedAsset
      ? this.miniMenuService.updateMediaCrop(owner, pending.expectedAsset.id, pending.crops, alt, pending.expectedAsset.mediaVersion)
      : pending.file && pending.dimensions
        ? this.miniMenuService.uploadPreparedMedia(owner, role, pending.file, pending.crops, alt,
          pending.expectedAsset?.id || 0, pending.operationId, pending.dimensions)
        : throwError(() => new Error('Prepare the photo again before saving.'));
    upload.subscribe({
      next: asset => { this.setPresentationMedia(pending.target, asset);
        if (role === 1) this.landingCrops = pending.crops;
        this.clearPendingPresentationSave(); this.isSaving = false; },
      error: error => { this.isSaving = false;
        this.toastr.error(ErrorHandlerService.bodyMessage(error) || 'The photo could not be saved. Please try again.'); }
    });
  }

  private uploadLandingBackground(
    businessPartnerId: number,
    file: File,
    crops: MiniMenuPromoCrops,
    dimensions?: { width: number; height: number }, operationId: string = crypto.randomUUID(), originalFile?: File
  ): void {
    this.isSaving = true;
    if (environment.miniMenuImagePipelineEnabled) {
      this.persistPresentationSave(businessPartnerId, { target: 'teaserBackgroundUrl', file, crops,
        dimensions, operationId, originalFile, expectedAsset: this.presentationMedia('teaserBackgroundUrl') });
      return;
    }
    const upload = environment.miniMenuImagePipelineEnabled && dimensions
      ? this.miniMenuService.uploadPreparedMedia(businessPartnerId, 1, file, crops, 'Landing background',
        this.presentationMedia('teaserBackgroundUrl')?.id || 0, operationId, dimensions)
      : this.miniMenuService.uploadMedia(businessPartnerId, 1, file).pipe(
      switchMap((asset) => environment.miniMenuImagePipelineEnabled ? of(asset) : this.miniMenuService.updateMediaCrop(
        businessPartnerId,
        asset.id,
        crops,
        'Landing background'
      ))
    );
    upload.subscribe({
      next: (asset) => {
        this.setPresentationMedia('teaserBackgroundUrl', asset);
        this.landingCrops = crops;
        this.isSaving = false;
      },
      error: (error) => {
        this.isSaving = false;
        this.toastr.error(error?.error?.message || 'The MiniMenu image could not be uploaded.');
      }
    });
  }

  /** Re-opens the crop step for the landing image already on file. */
  editLandingCrops(): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    const pending = this.pendingPresentationSave?.target === 'teaserBackgroundUrl' ? this.pendingPresentationSave : undefined;
    const asset = pending?.expectedAsset || this.presentationMedia('teaserBackgroundUrl');
    if (!businessPartnerId || this.isSaving || (!asset && !pending)
      || (!this.settings.teaserBackgroundUrl && !pending?.file)) {
      return;
    }
    if (pending?.file) {
      this.finishLandingCropEdit(businessPartnerId, asset, URL.createObjectURL(pending.file), pending, true);
      return;
    }

    if (environment.miniMenuImagePipelineEnabled) {
      this.isSaving = true;
      this.miniMenuService.getAdminMediaPreview(businessPartnerId, asset!).subscribe({
        next: blob => { this.isSaving = false; const url = URL.createObjectURL(blob);
          this.finishLandingCropEdit(businessPartnerId, asset, url, pending, true); },
        error: () => { this.isSaving = false; this.toastr.error('The photo could not be loaded.'); }
      });
      return;
    }
    this.finishLandingCropEdit(businessPartnerId, asset, this.settings.teaserBackgroundUrl, pending);
  }

  private finishLandingCropEdit(businessPartnerId: number, asset: MiniMenuMediaAsset | null, source: string,
    pending?: PendingPresentationSave, releaseSource = false): void {
    const modalRef = this.openLandingCropModal(source, pending?.crops || this.landingCrops);
    if (releaseSource) modalRef.result.finally(() => URL.revokeObjectURL(source)).catch(() => {});
    modalRef.result.then(
      (result: MiniMenuLandingCropResult) => {
        if (result.removed) {
          this.clearImage('teaserBackgroundUrl');
          return;
        }
        // A replacement is a fresh upload; otherwise only the crops changed.
        if (result.replacementFile) {
          this.uploadLandingBackground(businessPartnerId, result.replacementFile, result.crops, result.imageDimensions, result.imageOperationId);
          return;
        }
        if (pending?.file) {
          const operationId = JSON.stringify(pending.crops) === JSON.stringify(result.crops)
            ? pending.operationId : crypto.randomUUID();
          this.persistPresentationSave(businessPartnerId, { ...pending, crops: result.crops, operationId });
          return;
        }
        if (!asset) return;
        if (environment.miniMenuImagePipelineEnabled) {
          this.persistPresentationSave(businessPartnerId, { target: 'teaserBackgroundUrl', crops: result.crops,
            operationId: crypto.randomUUID(), expectedAsset: asset });
          return;
        }
        this.isSaving = true;
        this.miniMenuService.updateMediaCrop(businessPartnerId, asset.id, result.crops, 'Landing background')
          .subscribe({
            next: (updated) => {
              this.setPresentationMedia('teaserBackgroundUrl', updated);
              this.landingCrops = result.crops;
              this.isSaving = false;
              this.toastr.success('Landing background crops saved.');
            },
            error: (error) => {
              this.isSaving = false;
              this.toastr.error(error?.error?.message || 'The crop could not be saved.');
            }
          });
      },
      () => { /* dismissed — crops unchanged */ }
    );
  }

  clearImage(target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl'): void {
    const asset = this.presentationMedia(target);
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!asset || !businessPartnerId) {
      if (this.pendingPresentationSave?.target === target) this.clearPendingPresentationSave();
      this.setPresentationMedia(target, null);
      return;
    }
    this.miniMenuService.deleteMedia(businessPartnerId, asset.id).subscribe({
      next: () => {
        if (this.pendingPresentationSave?.target === target) this.clearPendingPresentationSave();
        this.setPresentationMedia(target, null);
      },
      error: () => this.toastr.error('The MiniMenu image could not be removed.')
    });
  }

  private patchFromSettings(): void {
    this.formGroup.patchValue({
      LocationTag: this.settings.locationTag,
      Eyebrow: this.settings.eyebrow,
      Subline: this.settings.subline
    });
    this.fontSelect = this.fonts.find((font) => font.id === this.settings.theme.font) || this.fonts[0];
    this.languageSelect = this.languages.find((lang) => lang.id === this.settings.defaultLanguage) || this.languages[0];
  }

  private collectIntoSettings(): void {
    this.settings.locationTag = String(this.formGroup.get('LocationTag')?.value || '').slice(0, 24);
    this.settings.eyebrow = this.formGroup.get('Eyebrow')?.value || '';
    this.settings.subline = this.formGroup.get('Subline')?.value || '';
    this.settings.theme.font = this.fontSelect?.id || 'hanken';
    this.settings.defaultLanguage = (this.languageSelect?.id as MiniMenuSettings['defaultLanguage']) || 'en';
  }

  menuTranslation(entityType: 1 | 2, entityId: number): MiniMenuMenuText {
    let record = this.translation.menu.find((item) =>
      item.entityType === entityType && item.entityId === entityId);
    if (!record) {
      record = { entityType, entityId, name: '', description: '' };
      this.translation.menu.push(record);
    }
    return record;
  }

  promoTranslation(promotionId: number): MiniMenuFoodieNewsText {
    let record = this.translation.foodieNews.find((item) => item.foodieNewsId === promotionId);
    if (!record) {
      record = {
        foodieNewsId: promotionId,
        badge: '',
        title: '',
        availabilityText: '',
        lead: '',
        body: ''
      };
      this.translation.foodieNews.push(record);
    }
    return record;
  }

  private presentationMedia(
    target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl'
  ): MiniMenuMediaAsset | null {
    if (!this.site?.presentation) {
      return null;
    }
    if (target === 'teaserBackgroundUrl') {
      return this.site.presentation.landingBackground;
    }
    return target === 'landingLogoUrl'
      ? this.site.presentation.landingLogo
      : this.site.presentation.bannerLogo;
  }

  private setPresentationMedia(
    target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl',
    asset: MiniMenuMediaAsset | null
  ): void {
    if (this.site?.presentation) {
      if (target === 'teaserBackgroundUrl') {
        this.site.presentation.landingBackground = asset;
      } else if (target === 'landingLogoUrl') {
        this.site.presentation.landingLogo = asset;
      } else {
        this.site.presentation.bannerLogo = asset;
      }
    }
    const previewKey = target === 'teaserBackgroundUrl'
      ? 'presentation-background'
      : target === 'landingLogoUrl'
        ? 'presentation-landing-logo'
        : 'presentation-banner-logo';
    this.revokeAdminMediaPreview(previewKey);
    if (target === 'teaserBackgroundUrl') {
      this.settings.teaserBackgroundUrl = null;
    } else {
      this.settings.theme[target] = null;
    }

    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (asset && businessPartnerId) {
      this.loadAdminMediaPreview(previewKey, businessPartnerId, asset, (url) => {
        if (target === 'teaserBackgroundUrl') {
          this.settings.teaserBackgroundUrl = url;
        } else {
          this.settings.theme[target] = url;
        }
      });
    }
  }

  private emptyTranslation(): MiniMenuTranslationBundle {
    return {
      languageCode: 'zh-CN',
      site: { locationTag: '', eyebrow: '', subline: '' },
      menu: [],
      promotions: [],
      foodieNews: []
    };
  }
}
